import React, {useCallback, useEffect, useState, useMemo, useRef} from 'react'
import _ from 'lodash'
import SearchBar from '@components/SearchBar/index.js'
import {Button, Colors, Panel, Row, IconButton, Popup, Stack} from '@components/UI/index.js'
import EditRouteTemplateModal from '@components/routes/EditRouteTemplateModal.js'
import {Customer, Reseller, User} from 'types/types.js'
import {NewRouteTemplate, RouteTemplate} from 'server/functions/route/route.types.js'
import {NewRouteTemplateModel} from 'server/models/routeTemplate.js'
import Reflux from 'reflux-react-16'
import OrderStore from 'src/stores/OrderStore.js'
import CustomerStore from 'src/stores/CustomerStore.js'
import OrderActions from 'src/actions/OrderActions.js'
import UserStore from 'src/stores/UserStore'
import Table from '@components/UI/table/index.js'
import {RRule} from 'rrule'
import languages from 'server/languages.js'
import t, {getLocale} from 'src/helpers/Languages.js'
import useVehicleStore from 'src/stores/VehicleStore.js'
import moment from 'moment'
import {Query} from '@components/SearchBar/SearchItem'
import filter from 'src/utils/filter'
import {getResellerMessengerSettings, normalizeAdjustableByMessengerGrouped, resolveMessengerRouteSettings} from '../../../utils/routeAdjustability.js'
import {getAvailableBranches} from '../../utils/branches.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import {formSpacing} from '../../components/UI/forms/index.js'
import type {PopupRef} from '../../components/UI/overlays/Popup.js'

type RouteTemplatesProps = {
    reseller: Reseller
    customers: {
        [customerId: string]: Customer
    }
    users: User[]
    routeTemplates: {
        [routeTemplateId: string]: RouteTemplate
    }
}

function RouteTemplates({reseller, customers, users, routeTemplates}: RouteTemplatesProps) {
    const vehicles = useVehicleStore((state) => state.vehicles)
    const getVehicles = useVehicleStore((state) => state.getVehicles)
    const [showEditRouteTemplateModal, setShowEditRouteTemplateModal] = useState(false)
    const [editRouteTemplate, setEditRouteTemplate] = useState<RouteTemplate | NewRouteTemplate>(null)
    const [selectedTemplates, setSelectedTemplates] = useState<RouteTemplate['_id'][]>([])
    const [error, setError] = useState<string | undefined>()
    const popupRef = useRef<PopupRef>(null)
    const [filterQueries, setFilterQueries] = useState<Query[]>([])
    const isMounted = useRef(false)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        getVehicles()
    }, [getVehicles])

    // The modal stays open while saving, so an error shows in it
    const onSaveRouteTemplate = useCallback((editDate?: string): Promise<void> => {
        setError(undefined)

        return new Promise((resolve, reject) => {
            if ('_id' in editRouteTemplate && editRouteTemplate._id) {
                OrderActions.updateRouteTemplate(editRouteTemplate, editDate, (err) => {
                    if (err) {
                        if (isMounted.current) {
                            setError(err)
                        } else {
                            alert(err)
                        }
                        reject(err)
                    } else {
                        if (isMounted.current) {
                            setError(undefined)
                            setShowEditRouteTemplateModal(false)
                            setEditRouteTemplate(null)
                        }
                        resolve()
                    }
                })
            } else {
                OrderActions.createRouteTemplate(editRouteTemplate, (err) => {
                    if (err) {
                        if (isMounted.current) {
                            setError(err)
                        } else {
                            alert(err)
                        }
                        reject(err)
                    } else {
                        if (isMounted.current) {
                            setError(undefined)
                            setShowEditRouteTemplateModal(false)
                            setEditRouteTemplate(null)
                        }
                        resolve()
                    }
                })
            }
        })
    }, [editRouteTemplate])

    const onRemoveRouteTemplates = useCallback(() => {
        if (selectedTemplates.length === 0) {
            return
        }

        popupRef.current?.open(t('deleteRouteTemplate'), t('deleteRouteTemplateText'), () => {
            popupRef.current.setLoading(true)
            OrderActions.removeRouteTemplates(selectedTemplates, (err) => {
                if (err) {
                    setError(err)
                } else {
                    setError(undefined)
                    setSelectedTemplates([])
                }
                popupRef.current.setLoading(false)
                popupRef.current?.close()
            })
        })
    }, [selectedTemplates, popupRef])

    const onClickAssignBranch = useCallback(async () => {
        const availableBranches = getAvailableBranches(reseller)

        popupRef.current?.setInputs([
            {label: t('branch'), name: 'branchId', value: '', type: 'select', options: [{value: '', title: t('noBranch')}, ...availableBranches.map((branch) => ({value: branch.id, title: branch.name}))]}
        ])

        const values = await popupRef.current?.open(t('assignBranch'), t('chooseBranchForRouteTemplates'))
        if (!values) return

        const branchId = values?.branchId || ''

        let success = 0
        const errors: string[] = []
        popupRef.current?.setProgress(0, selectedTemplates.length)

        for (const id of selectedTemplates) {
            const template = routeTemplates[id]
            if (!template) continue

            await new Promise<void>((resolve) => {
                OrderActions.updateRouteTemplate({...template, branchId}, moment().format('YYYY-MM-DD'), (err: string | null) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    popupRef.current?.setProgress(success + errors.length, selectedTemplates.length)
                    resolve()
                })
            })
        }

        setSelectedTemplates([])
        popupRef.current?.setMessage(`${success} route template${success === 1 ? '' : 's'} bewerkt.`)
        popupRef.current?.setErrors(errors)
    }, [selectedTemplates, routeTemplates, reseller])

    const columns = useMemo(() => [
        ...branchesAvailable(reseller) ?
            [{id: 'branchName', title: t('branch'), visible: false, key: 'branchName', width: 180, text: (template) => {
                const branch = reseller.settings.branches?.find((branch) => branch.id === template?.branchId)
                return branch?.name || ''
            }}] :
            [],
        {
            id: 'name',
            title: t('name'),
            visible: true,
            key: 'name',
            width: 150
        },
        {
            id: 'rrule',
            title: t('repetition'),
            key: 'rrule',
            visible: true,
            width: 250,
            text: (template: RouteTemplate): string => {
                if (template.rrule === '') return '-'

                const locale = getLocale()
                const language = locale === 'en' ? languages.ENGLISH : languages.DUTCH
                const text = `${RRule.fromString(template.rrule).toText((t) => {
                    return language.rrule[t as string] || t
                }, language)}`
                return _.capitalize(text)
            }
        },
        {
            id: 'vehicle',
            title: t('vehicle'),
            key: 'vehicle',
            visible: true,
            text: (template: RouteTemplate): string => {
                const vehicle = vehicles?.find((vehicle) => vehicle._id === template?.vehicleId)
                const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === template?.vehicleTypeId)
                const vehicleName = vehicle?.name || vehicleType?.name || ''
                return vehicleName || '-'
            },
            flex: 1
        },
        {
            id: 'messenger',
            title: t('messenger'),
            key: 'messenger',
            visible: true,
            text: (template: RouteTemplate): string => {
                const messenger = users.find((user) => user._id === template.messengerId)
                return messenger?.name || '-'
            },
            flex: 1
        },
        {
            id: 'startTime',
            title: t('startTime'),
            key: 'startTime',
            visible: true,
            width: 90
        },
        {
            id: 'startPoint',
            title: 'Startlocatie',
            key: 'startPoint',
            visible: true,
            text: (template: RouteTemplate): string => {
                return template.startPoint.name || `${template.startPoint.street} ${template.startPoint.nr}${template.startPoint.addition || ''}, ${template.startPoint.city}`
            },
            width: 150
        },
        {
            id: 'endPoint',
            title: 'Eindlocatie',
            key: 'endPoint',
            visible: true,
            text: (template: RouteTemplate): string => {
                return template.endPoint.name || `${template.endPoint.street} ${template.endPoint.nr}${template.endPoint.addition || ''}, ${template.endPoint.city}`
            },
            width: 150
        },
        {
            id: 'optimizeForTimeslots',
            title: t('timeslotOptimization'),
            key: 'optimizeForTimeslots',
            visible: true,
            text: (template: RouteTemplate): string => {
                return template.routeCalculation.optimizeForTimeslots ? t('yes') : t('no')
            },
            width: 150
        },
        {
            id: 'routeAdjustableByMessenger',
            title: t('adjustableByMessenger'),
            key: 'routeAdjustableByMessenger',
            visible: true,
            text: (template: RouteTemplate): string => {
                const messengerRouteSettings = resolveMessengerRouteSettings(template.routeCalculation, reseller.settings)
                return messengerRouteSettings.adjustableByMessenger ? t('yes') : t('no')
            },
            width: 175
        },
        {
            id: 'lastUpdated',
            title: t('lastUpdated'),
            key: 'lastUpdated',
            visible: true,
            text: (template: RouteTemplate): string => {
                const lastHistory = template.history.findLast((entry) => entry.timestamp)
                if (!lastHistory) {
                    return '-'
                }

                return moment(lastHistory.timestamp).format('YYYY-MM-DD HH:mm')
            },
            width: 150
        },
        {
            id: 'edit', visible: true, width: 50, showOnHover: true, render: (template: RouteTemplate) => {
                return (
                    <IconButton onClick={async () => {
                        OrderActions.getRouteTemplate(template._id, (err: string | null, routeTemplate?: RouteTemplate) => {
                            if (err) {
                                setError(err)
                                return
                            }

                            setEditRouteTemplate(routeTemplate)
                            setShowEditRouteTemplateModal(true)
                        })
                    }}
                    >
                        <i className='mdi mdi-pencil' />
                    </IconButton>
                )
            }
        }
    ], [reseller, vehicles, users])

    const onCopyCallback = useCallback(() => {
        const originalTemplateId = selectedTemplates[0]

        const originalTemplate = routeTemplates[originalTemplateId]

        if (!originalTemplate) {
            setError('Kan geselecteerde template niet vinden en kopiëren')
            return
        }

        const resellerMessengerSettings = getResellerMessengerSettings(reseller.settings)
        const originalRouteCalculation = originalTemplate.routeCalculation || {}
        const adjustableByMessengerGrouped = normalizeAdjustableByMessengerGrouped(originalRouteCalculation.adjustableByMessengerGrouped)

        const routeCalculation = {
            ...originalRouteCalculation,
            adjustableByMessengerGrouped,
            adjustableByMessenger: originalRouteCalculation.adjustableByMessenger ?? resellerMessengerSettings.adjustableByMessenger,
            allowAddAddress: originalRouteCalculation.allowAddAddress ?? resellerMessengerSettings.allowAddAddress,
            allowAddCollo: originalRouteCalculation.allowAddCollo ?? resellerMessengerSettings.allowAddCollo,
            messengerAddOrderWarning: originalRouteCalculation.messengerAddOrderWarning ?? resellerMessengerSettings.messengerAddOrderWarning,
            showWarningOnAddExistingOrder: originalRouteCalculation.showWarningOnAddExistingOrder ?? resellerMessengerSettings.showWarningOnAddExistingOrder,
            allowMessengerEditCustomOptions: originalRouteCalculation.allowMessengerEditCustomOptions ?? resellerMessengerSettings.allowMessengerEditCustomOptions
        }

        setEditRouteTemplate(NewRouteTemplateModel.parse({
            ...originalTemplate,
            routeCalculation
        }))
        setShowEditRouteTemplateModal(true)
    }, [selectedTemplates, routeTemplates, reseller])

    const filteredTemplates = useMemo(() => {
        return filter.routeTemplates(Object.values(routeTemplates), filterQueries, reseller, vehicles, users) as RouteTemplate[]
    }, [filterQueries, routeTemplates, reseller, vehicles, users])

    return (
        <Stack style={{flex: 1}}>
            <Panel style={{display: 'flex', borderTop: 'none', backgroundColor: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>
                <SearchBar
                    queries={filterQueries}
                    onQueryChange={(q) => setFilterQueries([...q])}
                    columns={columns.filter((column) => !!column.title)}
                />
                <Row style={{flexWrap: 'wrap', columnGap: formSpacing.columnGap, rowGap: formSpacing.rowGap}}>
                    <Button
                        variant='outline-white'
                        onClick={() => {
                            const vehicle = vehicles.find((v) => v._id === reseller.settings.defaultVehicle)
                            const defaultVehicleType = reseller?.settings.vehicleTypes.find((v) => v._id === (vehicle?.typeId || reseller.settings.defaultVehicle)) || reseller?.settings.vehicleTypes[0]
                            const resellerMessengerSettings = getResellerMessengerSettings(reseller.settings)

                            setEditRouteTemplate(NewRouteTemplateModel.parse({
                                reseller: reseller.name,
                                startPoint: reseller.settings.companyAddress,
                                endPoint: reseller.settings.companyAddress,
                                vehicleType: defaultVehicleType.routeProfile,
                                vehicleTypeId: defaultVehicleType._id,
                                vehicleId: vehicle?._id,
                                startTime: reseller?.settings?.account?.openingHours[moment().format('dddd').toLowerCase()] ?
                                    reseller.settings.account.openingHours[moment().format('dddd').toLowerCase()].from :
                                    '00:00',
                                routeCalculation: {
                                    optimizeForTimeslots: reseller.settings.optimizeForTimeslots,
                                    adjustableByMessengerGrouped: 'default',
                                    adjustableByMessenger: resellerMessengerSettings.adjustableByMessenger,
                                    allowAddAddress: resellerMessengerSettings.allowAddAddress,
                                    allowAddCollo: resellerMessengerSettings.allowAddCollo,
                                    messengerAddOrderWarning: resellerMessengerSettings.messengerAddOrderWarning,
                                    showWarningOnAddExistingOrder: resellerMessengerSettings.showWarningOnAddExistingOrder,
                                    allowMessengerEditCustomOptions: resellerMessengerSettings.allowMessengerEditCustomOptions
                                }
                            }))
                            setShowEditRouteTemplateModal(true)
                        }}
                        label={t('newRouteTemplate')}
                    />

                    {selectedTemplates.length === 1 && (
                        <Button
                            variant='outline-white'
                            onClick={onCopyCallback}
                            icon='mdi mdi-content-copy'
                        />
                    )}

                    {selectedTemplates.length > 0 && getAvailableBranches(reseller).length > 0 && (
                        <Button
                            variant='outline-white'
                            onClick={onClickAssignBranch}
                            icon='mdi mdi-office-building-marker-outline'
                            tooltip={t('assignBranch')}
                        />
                    )}

                    {selectedTemplates.length > 0 && (
                        <Button
                            variant='outline-white'
                            onClick={onRemoveRouteTemplates}
                            icon='mdi mdi-delete'
                        />
                    )}
                </Row>
            </Panel>

            <div style={{flex: 1, marginTop: 24, marginLeft: 24, marginRight: 24}}>
                <Table
                    tableName='RouteTemplates'
                    rows={filteredTemplates}
                    columns={columns}
                    selectedRows={selectedTemplates}
                    onChange={(selectedTemplates) => {
                        setSelectedTemplates(selectedTemplates)
                    }}
                />
            </div>

            {editRouteTemplate && showEditRouteTemplateModal && (
                <EditRouteTemplateModal
                    onClose={() => {
                        setShowEditRouteTemplateModal(false)
                        setEditRouteTemplate(null)
                        setError(undefined)
                    }}
                    template={editRouteTemplate}
                    onChange={(template) => setEditRouteTemplate({...template})}
                    onSave={onSaveRouteTemplate}
                    messengers={users}
                    reseller={reseller}
                    customers={customers}
                    error={error}
                />
            )}

            <Popup ref={popupRef} />
        </Stack>
    )
}

class RouteTemplatesWrapper extends Reflux.Component {
    constructor(props) {
        super(props)

        // @ts-expect-error reflux-react-16 is not typed
        this.stores = [OrderStore, CustomerStore, UserStore]
    }

    render() {
        // @ts-expect-error reflux-react-16 is not typed
        return <RouteTemplates {...this.props} {...this.state} />
    }
}

// Typed for the TSX that renders it, Reflux.Component isn't
export default RouteTemplatesWrapper as unknown as React.ComponentType<{reseller: Reseller, user?: User}>
